import { useState } from "react";
import { PDFIcon } from "../icons/pdf-icon";
import { cn, MimeTypes } from "../utls/utils";
import { ImageModal } from "./image-modal";
import { PDFViewer } from "./pdf-viewer";

interface ThumbnailProps {
  src: string;
  size?: "small" | "large";
  fileType?: MimeTypes;
  fileName?: string;
  allImages?: Array<{ url: string; fileName?: string; fileType?: MimeTypes }>;
  currentIndex?: number;
}

const getFileIcon = (fileType: string) => {
  // Check if it's a PDF file
  if (
    fileType === "application/pdf" ||
    fileType.toLowerCase().includes("pdf")
  ) {
    return <PDFIcon className="w-full h-full p-2 text-terracotta-600" />;
  }

  // Generic file icon for other types
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-full p-2"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <text x="12" y="16" textAnchor="middle" fontSize="4" fill="currentColor">
        {fileType.includes("/")
          ? fileType.split("/")[1].toUpperCase()
          : fileType.toUpperCase()}
      </text>
    </svg>
  );
};

export function Thumbnail({
  src,
  size = "small",
  fileType,
  fileName,
  allImages = [],
  currentIndex = 0,
}: ThumbnailProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

  const isImage = !fileType || fileType.startsWith("image/");
  const isPdf = fileType === MimeTypes.PDF;

  const sizeClasses = cn(
    "rounded",
    size === "small" && "w-[62px] h-[62px]",
    size === "large" && "w-[100px] h-[100px]"
  );

  // Check if we have a carousel of mixed file types
  const hasMultipleFiles = allImages.length > 1;
  
  // Find all PDFs in the carousel
  const pdfFiles = allImages.filter(img => 
    img.fileType === MimeTypes.PDF
  );
  
  // Find all images in the carousel
  const imageFiles = allImages.filter(img => 
    !img.fileType || img.fileType.startsWith("image/")
  );
  
  const hasPdfsOnly = hasMultipleFiles && pdfFiles.length === allImages.length;
  const hasImagesOnly = hasMultipleFiles && imageFiles.length === allImages.length;
  const hasMixedFileTypes = hasMultipleFiles && !hasPdfsOnly && !hasImagesOnly;

  // Format PDFs for the PDFViewer carousel
  const pdfFilesForViewer = pdfFiles.map(pdf => ({
    url: pdf.url,
    fileName: pdf.fileName || "PDF Document",
  }));

  const handleContentClick = () => {
    if (isImage) {
      setIsModalOpen(true);
    } else if (isPdf) {
      // If we have mixed file types, use ImageModal for unified carousel
      if (hasMixedFileTypes) {
        setIsModalOpen(true);
      } else {
        setIsPdfViewerOpen(true);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsPdfViewerOpen(false);
  };

  // Convert allImages to the format expected by ImageModal
  const modalImages =
    allImages.length > 0
      ? allImages.map((img) => ({
          url: img.url,
          fileName: img.fileName || "Image",
        }))
      : [];

  if (isImage || (isPdf && hasMixedFileTypes)) {
    return (
      <>
        {isImage ? (
          <img
            alt={fileName || ""}
            src={src}
            className={cn(
              sizeClasses,
              "object-cover cursor-pointer transition-transform hover:brightness-90",
              "hover:scale-[1.02]"
            )}
            onClick={handleContentClick}
            title="Click to enlarge"
          />
        ) : (
          <div className="flex flex-col items-center">
            <div
              className={cn(
                sizeClasses,
                "bg-cream-50 border border-beige-200 flex items-center justify-center",
                "cursor-pointer hover:bg-cream-100 transition-colors"
              )}
              onClick={handleContentClick}
              title="Click to view in carousel"
            >
              {getFileIcon(fileType || "file")}
            </div>
            <span className="mt-1 text-xs text-brown-600 truncate max-w-[100px]">
              {fileName || "Unknown file"}
            </span>
          </div>
        )}

        <ImageModal
          isOpen={isModalOpen}
          onClose={closeModal}
          imageSrc={src}
          imageAlt={fileName || "Image preview"}
          images={modalImages.length > 0 ? modalImages : undefined}
          currentIndex={currentIndex}
        />
      </>
    );
  }

  // Handling for PDF when there are only PDFs in the carousel or a single PDF
  if (isPdf) {
    return (
      <div className="flex flex-col items-center">
        <div
          className={cn(
            sizeClasses,
            "bg-cream-50 border border-beige-200 flex items-center justify-center",
            "cursor-pointer hover:bg-cream-100 transition-colors"
          )}
          onClick={handleContentClick}
          title="Click to view PDF"
        >
          {getFileIcon(fileType || "file")}
        </div>
        <span className="mt-1 text-xs text-brown-600 truncate max-w-[100px]">
          {fileName || "Unknown file"}
        </span>

        {/* PDF Viewer Modal */}
        <PDFViewer
          isOpen={isPdfViewerOpen}
          onClose={closeModal}
          pdfUrl={src}
          fileName={fileName}
          pdfFiles={hasPdfsOnly ? pdfFilesForViewer : undefined}
          currentIndex={hasPdfsOnly ? currentIndex : 0}
        />
      </div>
    );
  }

  return null;
}